import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { DEFAULT_PLAN, PLANS, type PlanId } from '../lib/pricing'

/** A Checkout Session to mount in the subscribe sheet (Stripe's embedded form). */
export interface EmbeddedSession {
  clientSecret: string
  publishableKey: string
}

// Calls the `billing` Edge Function. Checkout comes back as an embedded
// session the subscribe sheet mounts in place — paying never leaves the app —
// or, where the project has no publishable key configured, as the hosted
// Stripe page this tab is sent to. The Billing Portal is always Stripe's
// hosted page (there is no embeddable one). Same supabase.functions.invoke
// pattern as useRecipes.importRecipe: the user's session token rides along.
export function useBilling() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [session, setSession] = useState<EmbeddedSession | null>(null)

  // `plan` is optional because the Settings panel wires this straight to a button's
  // onClick, which hands us a click event rather than a plan — hence the
  // is-it-really-a-plan guard instead of trusting the argument. The subscribe
  // sheet, where the choice is actually made, passes a real PlanId and the
  // currency it displayed, so Checkout charges in that same currency
  // (the function only honours one the Prices carry — plans.ts).
  async function startCheckout(plan?: PlanId, currency?: string) {
    const chosen = PLANS.some((p) => p.id === plan) ? plan! : DEFAULT_PLAN
    const body = typeof currency === 'string' ? { plan: chosen, currency, embedded: true } : { plan: chosen, embedded: true }
    const data = await run('billing/checkout', body)
    if (data?.client_secret && data.publishable_key) {
      setSession({ clientSecret: data.client_secret, publishableKey: data.publishable_key })
    } else if (data?.url) {
      window.location.assign(data.url)
    } else if (data) {
      setError('Something went wrong — try again in a moment')
    }
  }

  async function openBillingPortal() {
    const data = await run('billing/portal')
    if (data?.url) window.location.assign(data.url)
    else if (data) setError('Something went wrong — try again in a moment')
  }

  async function run(name: string, body: Record<string, unknown> = {}) {
    setLoading(true)
    setError(null)
    const { data, error: fnError } = await supabase.functions.invoke<{ url?: string; client_secret?: string; publishable_key?: string }>(name, { body })
    setLoading(false)
    if (fnError || !data) {
      setError('Something went wrong — try again in a moment')
      return null
    }
    return data
  }

  /** Back to choosing a plan: the open session is dropped (Stripe expires it on its own). */
  const cancelCheckout = () => setSession(null)

  return { loading, error, session, startCheckout, cancelCheckout, openBillingPortal }
}
